<app-jump-nav [links]="jumpNavLinks" />

<!-- GOOGLE REVIEWS — the structured, star-rated, provenance-linked widget.
     Kept as bordered surface cards (stars + author + date + "Read on Google"
     don't fold cleanly into the pull-quote figure), but tied to the page's
     quote vocabulary by the shared amber left-rule and the kicker eyebrow. -->
<section
  class="google-reviews-section"
  aria-labelledby="google-reviews-heading"
>
  <header class="google-reviews-header">
    <p class="testimonials-kicker">Verified on Google</p>
    <h2 id="google-reviews-heading">Google reviews</h2>
    <p class="google-reviews-summary">
      <span class="google-reviews-stars" aria-hidden="true">
        @for (i of starsRange; track i) {
          <span class="google-reviews-star google-reviews-star-filled">★</span>
        }
      </span>
      <strong>{{ googleAggregate.averageRating }}</strong>
      from
      <strong>{{ googleAggregate.count }}</strong>
      reviews on
      <a
        href="https://www.google.com/maps/place/?q=place_id:ChIJQar5hj3Tt4kRzQFeHz_Me3g"
        target="_blank"
        rel="noopener"
      >
        Google
      </a>
    </p>
  </header>
  <ol class="testimonials-grid google-reviews-list">
    @for (review of visibleGoogleReviews(); track review.id) {
      <li
        class="google-review testimonial-card"
        grooveSurface
        variant="bordered"
        size="lg"
      >
        <div class="google-review-meta">
          <span class="google-review-author">{{ review.author }}</span>
          <span
            class="google-review-stars"
            role="img"
            [attr.aria-label]="'Rated ' + review.rating + ' out of 5 stars'"
          >
            @for (i of starsRange; track i) {
              <span
                class="google-review-star"
                [class.google-review-star-filled]="i <= review.rating"
                aria-hidden="true"
              >
                ★
              </span>
            }
          </span>
          <time class="google-review-date" [attr.datetime]="review.date">
            {{ review.date | date: "longDate" }}
          </time>
        </div>
        @if (review.text) {
          <p class="google-review-text">{{ review.text }}</p>
        } @else {
          <p class="google-review-text google-review-text-empty">
            (rating only, no written review)
          </p>
        }
        @if (review.url) {
          <a
            class="google-review-link"
            [href]="review.url"
            target="_blank"
            rel="noopener nofollow"
          >
            Read on Google ↗
          </a>
        }
      </li>
    }
  </ol>
  @if (!showAllGoogle() && googleReviews.length > INITIAL_GOOGLE_COUNT) {
    <p class="testimonials-show-more">
      <button
        grooveAction
        variant="outline"
        type="button"
        (click)="toggleAllGoogle()"
      >
        Show all {{ googleReviews.length }} reviews
      </button>
    </p>
  }
</section>

<!-- VIDEO TESTIMONIALS — students in their own voice. Consistent kicker +
     heading; the youtube placeholders carry their own surface treatment. -->
<section class="video-testimonials-section" aria-labelledby="video-testimonial">
  <header class="testimonials-section-header">
    <p class="testimonials-kicker">See &amp; hear it</p>
    <h2 id="video-testimonial">Video testimonials</h2>
  </header>
  <div class="video-testimonials-list">
    <app-youtube-section
      resource="video:dw2Ske9ruJs"
      placeholderWebpImage="false"
      placeholderPriority
    />
    <app-youtube-section
      resource="video:MofjKm8Vejs"
      placeholderWebpImage="false"
    />
  </div>
</section>

<!-- WRITTEN TESTIMONIALS — the editorial centerpiece. All testimonials render
     as a masonry of boxed pull-quotes. One wavy-underline kicker lives here,
     on the page's centerpiece. -->
<section
  class="written-testimonials-section"
  aria-labelledby="written-testimonials"
>
  <header class="testimonials-section-header">
    <p class="testimonials-kicker">
      In their <span class="groove-underline-wavy">own words</span>
    </p>
    <h2 id="written-testimonials">Written testimonials</h2>
  </header>

  <div class="testimonials-grid written-testimonials-list">
    @for (t of visibleWrittenTestimonials(); track $index) {
      <figure grooveSurface variant="bordered" size="lg" class="written-quote">
        <blockquote>{{ t.text }}</blockquote>
        <figcaption>
          {{ t.author }}
          @if (t.date) {
            <span class="written-quote-date"> · {{ t.date }}</span>
          }
        </figcaption>
      </figure>
    }
  </div>
  @if (!showAllWritten() && gridWrittenCount > INITIAL_WRITTEN_COUNT) {
    <p class="testimonials-show-more">
      <button
        grooveAction
        variant="outline"
        type="button"
        (click)="toggleAllWritten()"
      >
        Show all {{ gridWrittenCount }} testimonials
      </button>
    </p>
  }
</section>
